import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  createPersona, 
  updatePersona, 
  deletePersona 
} from '../services/personaService';
import { 
  CreatePersonaRequest, 
  UpdatePersonaRequest 
} from '../types/personas';

/**
 * Hook for creating a new persona
 * @returns Mutation object for creating a persona
 */
export const useCreatePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaData: CreatePersonaRequest) => createPersona(personaData),
    onSuccess: () => {
      // Invalidate and refetch personas queries
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    }
  });
};

/**
 * Hook for updating an existing persona
 * @returns Mutation object for updating a persona
 */
export const useUpdatePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaData: UpdatePersonaRequest) => updatePersona(personaData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch personas queries
      queryClient.invalidateQueries({ queryKey: ['personas'] });
      
      // Update the specific persona in the cache
      queryClient.invalidateQueries({ queryKey: ['persona', variables.id] });
    }
  });
};

/**
 * Hook for deleting a persona
 * @returns Mutation object for deleting a persona
 */
export const useDeletePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaId: string) => deletePersona(personaId),
    onSuccess: (_, personaId) => {
      // Invalidate and refetch personas queries
      queryClient.invalidateQueries({ queryKey: ['personas'] });
      
      // Remove the specific persona from the cache
      queryClient.removeQueries({ queryKey: ['persona', personaId] });
    }
  });
}; 